import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Typography, Grid, Button, Tooltip } from '@mui/material';
import { fCurrency } from '../../../../../utils/formatNumber';
import Iconify from '../../../../../reusable-components/iconify';
import { CartsContext } from './_CartsContext';

CartTableToolbar.propTypes = {
  cart: PropTypes.object,
  facilityId: PropTypes.string,
  showTableCheckbox: PropTypes.bool,
  canViewCart: PropTypes.bool,
};

export default function CartTableToolbar({ cart, canViewCart, facilityId, showTableCheckbox }) {
  const navigate = useNavigate();
  const { totals, setCheckoutCart, unsavedChanges } = useContext(CartsContext);
  const categoryRef = useRef(null);
  const [isCategoryTruncated, setIsCategoryTruncated] = useState(false);

  useEffect(() => {
    const el = categoryRef.current;
    if (!el) return undefined;

    const checkTruncation = () => setIsCategoryTruncated(el.scrollWidth > el.clientWidth);
    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [cart?.category]);

  return (
    <Grid container direction="row" wrap="nowrap" justifyContent="space-between" alignItems="flex-end">
      <Grid item sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flexShrink: 1 }}>
        <Iconify icon={cart?.icon} width={35} height={35} style={{ flexShrink: 0 }} />
        <Tooltip arrow title={isCategoryTruncated ? cart?.category : ''}>
          <Typography ref={categoryRef} variant="subtitle1" noWrap sx={{ ml: 1, mr: 2, minWidth: 0, lineHeight: 1 }}>
            {cart?.category}
          </Typography>
        </Tooltip>
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ mr: 2, flexShrink: 0, fontSize: '14px', fontWeight: 'normal', lineHeight: 1 }}
        >
          Subtotal ({totals[cart.categoryId]?.items.length} item{totals[cart.categoryId]?.items.length !== 1 ? 's' : ''}
          ): {fCurrency(totals[cart.categoryId]?.totalPrice)}
        </Typography>
      </Grid>

      <Grid item sx={{ display: 'flex', alignItems: 'end', pr: 1, overflow: 'hidden', flexShrink: 1 }}>
        {!showTableCheckbox && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() =>
              navigate(
                `/dashboard/orders/new-order/${facilityId}?categoryId=${cart?.categoryId}&category=${encodeURIComponent(
                  cart?.category
                )}`
              )
            }
            disabled={unsavedChanges}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap', mr: 1 }}
          >
            Continue shopping
          </Button>
        )}
        {canViewCart && (
          <Tooltip arrow title={cart?.orderable ? '' : "You don't have permission to order for this category"}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => setCheckoutCart(cart.categoryId)}
              disabled={showTableCheckbox || unsavedChanges || !cart?.orderable}
              sx={{
                flexShrink: 1,
                minWidth: 0,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Checkout {cart?.category} cart
            </Button>
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );
}
